"use client"

import { useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  parseNum,
} from "./calculator-ui"

/**
 * Shared "solve for any one of three" shell for the ratio metrics — CPM, CTR,
 * and anything else shaped like `rate = numerator ÷ denominator × scale`.
 *
 * The maths and every word of copy come from the caller (see `cpm-widget` and
 * `ctr-widget`); this component only owns the tab-per-unknown layout and the
 * input state, so each page keeps its own captions and warnings rather than a
 * paraphrased template.
 *
 * Input state is shared across tabs on purpose: switching from "solve for CPM"
 * to "solve for impressions" keeps the numbers the visitor already typed.
 */

export interface SolveForField<K extends string> {
  key: K
  /** Tab label, e.g. "CPM". */
  tabLabel: string
  /** Input label, e.g. "Impressions". */
  label: string
  /** Label for the result card when this field is the unknown. */
  resultLabel: string
  defaultValue: string
  prefix?: string
  suffix?: string
  step?: string | number
  format: (n: number) => string
}

export interface SolveForResult {
  value: number
  caption: string
  tone?: "neutral" | "good" | "warn" | "bad"
}

export function SolveForWidget<K extends string>({
  title,
  idPrefix,
  fields,
  solve,
  invalidCaption,
  formulaNote,
}: {
  title: string
  idPrefix: string
  fields: readonly [SolveForField<K>, SolveForField<K>, SolveForField<K>]
  /** Return null when the inputs cannot produce a result (blank, zero divisor). */
  solve: (target: K, values: Record<K, number | null>) => SolveForResult | null
  invalidCaption: (target: K) => string
  formulaNote: (target: K) => string
}) {
  const [raw, setRaw] = useState<Record<K, string>>(
    () =>
      Object.fromEntries(fields.map((f) => [f.key, f.defaultValue])) as Record<
        K,
        string
      >,
  )

  const values = Object.fromEntries(
    fields.map((f) => [f.key, parseNum(raw[f.key])]),
  ) as Record<K, number | null>

  return (
    <CalculatorCard title={title}>
      <Tabs defaultValue={fields[0].key}>
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">Solve for</span>
          <TabsList>
            {fields.map((f) => (
              <TabsTrigger key={f.key} value={f.key}>
                {f.tabLabel}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {fields.map((target) => {
          const result = solve(target.key, values)
          const ok = result !== null && Number.isFinite(result.value)
          return (
            <TabsContent key={target.key} value={target.key}>
              <CalculatorGrid>
                {fields
                  .filter((f) => f.key !== target.key)
                  .map((f) => (
                    <NumberField
                      key={f.key}
                      id={`${idPrefix}-${target.key}-${f.key}`}
                      label={f.label}
                      value={raw[f.key]}
                      onChange={(next) =>
                        setRaw((prev) => ({ ...prev, [f.key]: next }))
                      }
                      prefix={f.prefix}
                      suffix={f.suffix}
                      step={f.step}
                    />
                  ))}
              </CalculatorGrid>
              <div className="mt-6">
                <ResultStat
                  label={target.resultLabel}
                  value={ok ? target.format(result.value) : "—"}
                  tone={ok ? (result.tone ?? "neutral") : "neutral"}
                  caption={ok ? result.caption : invalidCaption(target.key)}
                />
              </div>
              <FormulaNote>{formulaNote(target.key)}</FormulaNote>
            </TabsContent>
          )
        })}
      </Tabs>
    </CalculatorCard>
  )
}
